/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import type { MouseEvent } from 'react';
import * as React from 'react';

import { Typography2 } from '@coursera/cds-core';
import { ChevronPreviousIcon } from '@coursera/cds-icons';

import CheckSvg from 'bundles/video-player/assets/svg/CheckSvg';
import ModernSettingsMenuItemList from 'bundles/video-player/components/modernSettingsMenu/ModernSettingsMenuItemList';
import type { MenuItemList } from 'bundles/video-player/components/modernSettingsMenu/types';

import _t from 'i18n!nls/video-player';

const styles = {
  menuIconContainer: css`
    display: flex;
    align-items: center;
    width: 28px;
    height: 28px;
  `,

  submenuTitleContainer: css`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: var(--cds-spacing-100) var(--cds-spacing-200) var(--cds-spacing-200) var(--cds-spacing-200);
    border-bottom: 1px solid rgb(255 255 255 / 24%);
    margin-bottom: var(--cds-spacing-100);
  `,
  submenuTitleButton: css`
    width: 100%;
  `,
  submenuTitleContainerHoverFocus: css`
    && {
      background-color: transparent;
      width: 100%;
      display: flex;
      align-items: center;
      gap: var(--cds-spacing-150);
      padding: var(--cds-spacing-100) var(--cds-spacing-200);

      &:hover {
        background-color: rgb(255 255 255 / 12%);
        color: var(--cds-color-neutral-primary-invert);
      }
    }
  `,
  submenuSectionContainerButtonFocus: css`
    &:focus-visible {
      outline: 2px solid #79f;
    }
  `,
  submenuTitle: css`
    display: flex;
  `,
  boldIcon: css`
    /* The CDS chevron is not bold enough, so we need to override the styling */
    fill: var(--cds-color-neutral-background-primary);
    stroke: var(--cds-color-neutral-background-primary);
    stroke-width: 1px;
  `,
};

type ModernSettingsSubmenuProps = MenuItemList & {
  onBackButtonClick: (ev: MouseEvent<HTMLButtonElement>) => void;
  visible: boolean;
};

const ModernSettingsSubmenu = ({
  title,
  selected: initialSelectedOption,
  options,
  action,
  onBackButtonClick,
  visible,
}: ModernSettingsSubmenuProps) => {
  const settingsSubmenuConfig = options.map((option) => {
    const isSelected = option.id === initialSelectedOption?.id;
    return {
      title: option.label,
      icon: isSelected ? <CheckSvg /> : undefined,
      isSelected,
      action: () => {
        action(option);
      },
      role: option.role,
    };
  });

  return (
    <div>
      <button
        type="button"
        onClick={onBackButtonClick}
        aria-label={_t('Go back to main settings menu')}
        css={styles.submenuTitleButton}
      >
        <section
          css={[
            styles.submenuTitleContainer,
            styles.submenuTitleContainerHoverFocus,
            styles.submenuSectionContainerButtonFocus,
          ]}
        >
          <div css={styles.menuIconContainer}>
            <ChevronPreviousIcon size="small" css={styles.boldIcon} />
          </div>
          <Typography2 css={styles.submenuTitle} color="invertBody" component="p" variant="subtitleMedium">
            {title}
          </Typography2>
        </section>
      </button>

      <ModernSettingsMenuItemList visible={visible} menuConfig={settingsSubmenuConfig} />
    </div>
  );
};

export default ModernSettingsSubmenu;
